import type { ResourcePreview } from '../../../website/src/playbooks/guideTypes'

export const resourceCatalog: ResourcePreview[] = [
  {
    "id": "spooking-under-saddle",
    "title": "Fixing Spooking Under Saddle",
    "subtitle": "Respond to fear and distraction with clarity, patience, and better timing.",
    "introduction": "Respond to fear and distraction with clarity, patience, and better timing.",
    "category": "Horse behavior",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "response",
    "visualSteps": [
      {
        "title": "Pause and observe",
        "text": "Treat the spook as information. Notice the horse’s body, attention, and surroundings."
      },
      {
        "title": "Fear",
        "text": "Create space and time for curiosity. Do not force an approach."
      },
      {
        "title": "Distraction",
        "text": "Allow a brief look, then calmly ask for attention back."
      },
      {
        "title": "Reconnect",
        "text": "Reward a small return to attention or relaxation."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Redefine What Spooking Really Means",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Spooking is not always terror. Sometimes it is true fear. Sometimes it is distraction. Your first job is to determine which response you are seeing."
        },
        {
          "type": "paragraph",
          "text": "If you assume every spook is bad behavior, you may respond too harshly and break trust. Slow down long enough to read what your horse is communicating."
        }
      ]
    },
    "nextTitle": "Check the Four Factors Behind Spooking"
  },
  {
    "id": "unwanted-behavior",
    "title": "Fixing Unwanted Behavior",
    "subtitle": "Look underneath the behavior. Find a clearer next step.",
    "introduction": "Look underneath the behavior. Find a clearer next step.",
    "category": "Horse behavior",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "factors",
    "visualSteps": [
      {
        "title": "The human",
        "text": "Notice your own tension, posture, and expectations."
      },
      {
        "title": "The horse",
        "text": "Consider the individual horse, including comfort and soundness."
      },
      {
        "title": "The dynamics",
        "text": "Check the timing and clarity of your communication."
      },
      {
        "title": "The environment",
        "text": "Look for changes around you: other horses, footing, noise, or movement."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Recognize the Root of Unwanted Behavior",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Pawing, fidgeting, anxiety, or pushy habits are symptoms, not the core issue."
        },
        {
          "type": "paragraph",
          "text": "The real solution is understanding why the behavior shows up."
        },
        {
          "type": "paragraph",
          "text": "Coach's Tip: Don't get stuck reacting to the behavior itself. Always look underneath it."
        },
        {
          "type": "paragraph",
          "text": "Why This Matters: When you fix the cause, the behavior often disappears on its own."
        }
      ]
    },
    "nextTitle": "Use the 4 Factors Behind Every Behavior"
  },
  {
    "id": "groundwork",
    "title": "The Groundwork Playbook",
    "subtitle": "Six steps to a better horse and a clearer partnership.",
    "introduction": "Six steps to a better horse and a clearer partnership.",
    "category": "Groundwork",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "steps",
    "visualSteps": [
      {
        "title": "Understanding the Horse",
        "text": "Start by reading your horse’s attention and body language."
      },
      {
        "title": "Build Rapport, Focus, and Attention",
        "text": "Create connection through a calm, consistent presence."
      },
      {
        "title": "Follow the Leader",
        "text": "Bring clarity to the space you share."
      },
      {
        "title": "Use the Hindquarters for Attention",
        "text": "Help attention return without adding unnecessary tension."
      },
      {
        "title": "Lunging for Better Gaits",
        "text": "Look for rhythm and balance as you communicate on the circle."
      },
      {
        "title": "Build Confidence Through Obstacles",
        "text": "Introduce manageable challenges and give your horse time to investigate."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Understanding the Horse",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Your horse already has a language, and it is our job to learn it. Too often we focus on trying to get the horse to understand us, but real progress begins when we first understand them."
        },
        {
          "type": "paragraph",
          "text": "Horses communicate through energy first, then body language. If your energy is scattered or inconsistent, your horse will notice that before they ever notice your cues. Work on staying calm, grounded, and clear."
        },
        {
          "type": "paragraph",
          "text": "Raise your energy: Take a strong breath, stand taller, and use more intentional posture to ask for movement."
        },
        {
          "type": "paragraph",
          "text": "Lower your energy: Soften your shoulders and breathe deeply to invite relaxation."
        },
        {
          "type": "paragraph",
          "text": "Adapt your approach: Give a nervous horse slower steps and reassurance. Give a laid-back horse greater clarity and energy."
        },
        {
          "type": "paragraph",
          "text": "Every horse has a personality. Spend time observing and adjusting, and you will become fluent in your horse's language much faster."
        }
      ]
    },
    "nextTitle": "Build Rapport, Focus, and Attention"
  },
  {
    "id": "rider-confidence",
    "title": "The Rider’s Confidence Playbook",
    "subtitle": "Build calm, work through fear, and ride with clarity.",
    "introduction": "Build calm, work through fear, and ride with clarity.",
    "category": "Rider confidence",
    "offer": {
      "title": "Build confidence with a clear plan.",
      "description": "Turn these ideas into habits with the Confidence Accelerator and weekly coaching from Scott.",
      "label": "Explore the Confidence Accelerator",
      "path": "/accelerator"
    },
    "visual": "confidence",
    "visualSteps": [
      {
        "title": "Stand and breathe",
        "text": "Begin beside your horse in a place where you both feel steady."
      },
      {
        "title": "Walk together",
        "text": "Choose a small, familiar groundwork task."
      },
      {
        "title": "Mount when ready",
        "text": "Only move on when you and your horse are ready for the next step."
      },
      {
        "title": "One quiet lap",
        "text": "Make the task small enough to do with clarity."
      },
      {
        "title": "Add one task",
        "text": "Build gradually from a step that feels manageable."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Shift Your Focus, Shift Your Confidence",
      "blocks": [
        {
          "type": "paragraph",
          "text": "When fear shows up, it has a funny way of taking over the entire picture. Your mind zooms in on the what-ifs, your body tightens, and your horse feels that shift. Fear itself is not the real problem. It is what you choose to focus on that either feeds it or quiets it."
        },
        {
          "type": "paragraph",
          "text": "If you stare at what scares you, your body prepares for that outcome. If you redirect your focus toward where you want to go, your mind starts sending your body a different message, and your horse follows that new energy."
        },
        {
          "type": "paragraph",
          "text": "When you catch yourself staring at the fear, you do not need to fight it. You simply need to redirect your focus."
        },
        {
          "type": "paragraph",
          "text": "Try this: Pick a single point ahead of you. A fence post or a corner of the arena works perfectly."
        },
        {
          "type": "paragraph",
          "text": "Breathe toward that point. Let your eyes lead your body."
        },
        {
          "type": "paragraph",
          "text": "As your eyes soften and move forward, your mind follows. Then your body softens. Then your horse softens."
        },
        {
          "type": "paragraph",
          "text": "This small shift becomes a break in the fear pattern. You are no longer staring at the problem. You are focused on the path."
        }
      ]
    },
    "nextTitle": "Physiology First: Your Body Sets the Tone"
  },
  {
    "id": "hindquarter-engagement",
    "title": "Hindquarter Engagement",
    "subtitle": "Build reach, balance, and connection from the ground to the saddle.",
    "introduction": "The purpose of this guide is to help your horse find true engagement of the hindquarters. Engagement is more than just moving forward. It is about the horse stepping under with the hind legs, lifting the back, freeing the shoulders, and carrying themselves with balance and power. These six steps start on the ground and build all the way to simple engagement under saddle. The exercises are progressive. Each one lays the groundwork for the next, creating strength, mobility, and understanding in your horse. Take your time with each step. Do not rush. Progress comes from consistency and clarity, not speed.",
    "category": "Movement & balance",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "horse-back",
    "visualSteps": [],
    "firstSection": {
      "id": "step-1",
      "title": "Stepping Up",
      "blocks": [
        {
          "type": "note",
          "text": "Use these exercises with a comfortable, sound horse. If movement changes or pain is suspected, seek veterinary guidance before asking for more."
        },
        {
          "type": "paragraph",
          "text": "This first step lays the foundation for hindquarter engagement. Before we can create improvement, we need to establish a baseline. Every horse has a natural way of moving. Some are forward, others are a bit lazy, and most fall somewhere in between. Our goal is to see where your horse is starting from and then begin teaching them how to bring their hind legs further underneath the body."
        },
        {
          "type": "heading",
          "text": "How to do it"
        },
        {
          "type": "list",
          "items": [
            "1. Use a 15-foot line and a dressage whip. Toss the line over the horse’s neck so it is out of the way and hold near the snap for control.",
            "2. First make sure your horse is not afraid of the whip. Rub it along their body until they stay relaxed.",
            "3. Begin by walking your horse forward as you back in a small circle. Use gentle taps on the belly or flank to encourage them to step underneath.",
            "4. Watch closely. Are the hind feet reaching up toward the front footprints, or falling short? This tells you your horse’s baseline.",
            "5. From that baseline, start asking for a little more reach and mobility. Reward every time they increase the step. If your horse speeds up instead of stepping deeper, simply use your lead hand to slow them and redirect that effort into engagement, not speed."
          ]
        },
        {
          "type": "note",
          "text": "Key point: Speed is not engagement. Engagement is when the hind legs step further under the body, lifting the back and preparing the horse to carry weight more effectively."
        }
      ]
    },
    "nextTitle": "Flexion"
  },
  {
    "id": "herd-bound",
    "title": "Fixing Herd-Bound Behavior",
    "subtitle": "Build confidence in small moments away from the herd.",
    "introduction": "Build confidence in small moments away from the herd.",
    "category": "Horse behavior",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "steps",
    "visualSteps": [
      {
        "title": "Create a Safe Separation Space",
        "text": "Choose a manageable distance and a safe, familiar setting."
      },
      {
        "title": "Allow Exposure to Horses Coming and Going",
        "text": "Make departures and returns a quiet, predictable experience."
      },
      {
        "title": "Build the Human-Horse Relationship",
        "text": "Build connection once your horse is able to settle."
      },
      {
        "title": "A Simple Challenge to Try Today",
        "text": "Look for one small moment of attention and reward it."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Create a Safe Separation Space",
      "blocks": [
        {
          "type": "note",
          "text": "Begin with a separation your horse can manage calmly. If distress increases, reduce the distance or duration and get help; do not leave a panicking horse to work it out alone."
        },
        {
          "type": "list",
          "items": [
            "Place the horse in a secure area away from the herd.",
            "Choose a spot where they cannot see herd mates if possible. Partial line of sight can still work.",
            "Keep the environment quiet and low pressure so they can begin to settle on their own."
          ]
        },
        {
          "type": "note",
          "text": "Coach’s Tip: The goal is a calm, contained space where the horse can downshift without constant input from you."
        },
        {
          "type": "note",
          "text": "Coach’s Recommendation: Provide plenty of hay and clean water. Chewing and sipping are natural self-soothing behaviors, and a slow-feed hay net can extend the calming effect."
        },
        {
          "type": "note",
          "text": "Why This Matters: Limiting herd contact while supporting comfort needs teaches the horse to self-soothe and discover that they can feel safe without the herd."
        }
      ]
    },
    "nextTitle": "Allow Exposure to Horses Coming and Going"
  },
  {
    "id": "bucking",
    "title": "Fixing Bucking",
    "subtitle": "Read the cause before you respond to the behavior.",
    "introduction": "Read the cause before you respond to the behavior.",
    "category": "Horse behavior",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "factors",
    "visualSteps": [
      {
        "title": "The human",
        "text": "Notice your own tension, posture, and expectations."
      },
      {
        "title": "The horse",
        "text": "Consider the individual horse, including comfort and soundness."
      },
      {
        "title": "The dynamics",
        "text": "Check the timing and clarity of your communication."
      },
      {
        "title": "The environment",
        "text": "Look for changes around you: other horses, footing, noise, or movement."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Understand What Bucking Really Is",
      "blocks": [
        {
          "type": "note",
          "text": "If bucking is new, severe, or associated with discomfort, have pain and tack fit assessed before training. The exercises here are for a controlled session, not instructions for riding through an active buck."
        },
        {
          "type": "list",
          "items": [
            "Bucking is a symptom, not the root problem.",
            "Common causes:",
            "Excess energy",
            "Pain or discomfort (tack fit, soreness, injury)",
            "Confusion or unclear communication",
            "Fear or environmental stressors"
          ]
        },
        {
          "type": "note",
          "text": "Coach’s Tip: Don’t label bucking as “bad behavior.” It is your horse’s way of saying, “Something feels wrong.”"
        },
        {
          "type": "note",
          "text": "Why This Matters: If you only fight the buck, you miss the message. Listen for what is underneath it, and the problem becomes easier to solve."
        }
      ]
    },
    "nextTitle": "Check the 4 Factors Behind Bucking"
  },
  {
    "id": "treats-and-tension",
    "title": "Using Treats to Reduce Tension",
    "subtitle": "Use thoughtful placement and timing to support relaxation.",
    "introduction": "Use thoughtful placement and timing to support relaxation.",
    "category": "Groundwork",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "visual": "steps",
    "visualSteps": [
      {
        "title": "Understand the Purpose of Treats",
        "text": "Notice tension before deciding how to use a reward."
      },
      {
        "title": "How to Offer the Treat for Relaxation",
        "text": "Think about where the reward invites your horse to move."
      },
      {
        "title": "Incorporate Treats Sparingly and Temporarily",
        "text": "Use food thoughtfully, then build on clear communication and other rewards."
      },
      {
        "title": "A Simple Challenge to Try Today",
        "text": "Reward the response you want to repeat."
      },
      {
        "title": "Keep the Big Picture in Mind",
        "text": "Build on relaxed, attentive moments."
      }
    ],
    "firstSection": {
      "id": "step-1",
      "title": "Understand the Purpose of Treats",
      "blocks": [
        {
          "type": "list",
          "items": [
            "Treats are not just rewards — they can be tools to reduce tension.",
            "Offering a treat in the right way encourages a horse to lower their head and neck."
          ]
        },
        {
          "type": "paragraph",
          "text": "Watch the whole horse: a lower head on its own does not prove relaxation."
        },
        {
          "type": "note",
          "text": "Coach’s Tip: Think of treats as a training tool, not just a snack."
        },
        {
          "type": "note",
          "text": "Why This Matters: A lowered head creates both physical and mental relaxation."
        }
      ]
    },
    "nextTitle": "How to Offer the Treat for Relaxation"
  },
  {
    "id": "strong-back",
    "title": "The Strong Back Playbook",
    "subtitle": "Understand the support underneath the back. Build quality before difficulty.",
    "category": "Movement & balance",
    "visual": "horse-back",
    "visualSteps": [],
    "introduction": "When someone tells you your horse needs a stronger back, the next question should be: what are we actually asking the horse to do? Start by understanding how the body supports itself, then build a few good movements into a consistent routine.",
    "offer": {
      "title": "Understand what your horse is telling you.",
      "description": "Bring your questions to Horse Sense, Scott’s live weekly coaching class, and get clearer about what to work on next.",
      "label": "Explore Horse Sense coaching",
      "path": "/horse-sense"
    },
    "firstSection": {
      "id": "step-1",
      "title": "The back works as a system",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Think of the back and the muscles underneath the body as parts of one support system. The bow-and-string idea is a useful starting picture: the abdomen helps support the trunk, while the muscles around the spine help control and stabilize movement."
        },
        {
          "type": "paragraph",
          "text": "That does not mean the muscles on top of the back do nothing. The job changes with the exercise. Watch for controlled movement and a comfortable horse, rather than trying to force a particular shape."
        },
        {
          "type": "note",
          "text": "Research note: a 2023 study of nine horses measured electrical muscle activity during mobilization exercises. Abdominal activity increased with more demanding reaches. That is evidence of activity during an exercise, not proof that the hardest reach is best for every horse. [1]"
        }
      ]
    },
    "nextTitle": "Start with comfort",
    "previewSources": [
      {
        "title": "[1] Aulinas Coll, Blake & Ferro de Godoy (2023): muscle activity and lumbosacral movement during core exercises",
        "url": "https://writtle.repository.guildhe.ac.uk/id/eprint/15242/"
      }
    ]
  },
  {
    "id": "baggage-at-the-barn",
    "title": "The Baggage You Bring to the Barn",
    "subtitle": "Notice what you bring into the interaction. Give yourself and your horse a clearer start.",
    "category": "Rider awareness",
    "visual": "reset",
    "visualSteps": [
      {
        "title": "Notice",
        "text": "Name what is taking up your attention today."
      },
      {
        "title": "Breathe",
        "text": "Give yourself a few slow, comfortable breaths."
      },
      {
        "title": "Soften",
        "text": "Check your jaw, shoulders, and hands."
      },
      {
        "title": "Choose",
        "text": "Set one manageable intention for the session."
      }
    ],
    "introduction": "You can arrive at the barn carrying an entire day with you. The horse does not know the story behind it, but your interaction starts before you pick up the reins. Begin by noticing what you can change.",
    "offer": {
      "title": "Build confidence with a clear plan.",
      "description": "Turn these ideas into habits with the Confidence Accelerator and weekly coaching from Scott.",
      "label": "Explore the Confidence Accelerator",
      "path": "/accelerator"
    },
    "firstSection": {
      "id": "step-1",
      "title": "Check the person holding the rope",
      "blocks": [
        {
          "type": "paragraph",
          "text": "Before you ask the horse to settle, notice yourself. Are you rushing? Holding your breath? Gripping before the horse has done anything? Those are useful things to catch."
        },
        {
          "type": "paragraph",
          "text": "In the Four Factors, this is the Human. It is one part of the picture, alongside the Horse, the Dynamics, and the Environment. Your awareness matters, but every difficult moment is not your fault."
        },
        {
          "type": "note",
          "text": "Start with one adjustment: soften your hands, release your jaw, or take a comfortable breath. You do not have to solve the whole day before you can have a useful session."
        }
      ]
    },
    "nextTitle": "What your horse may be noticing"
  }
]
